import { z } from 'zod/v4';

export const UpdateQuestionnaireBuilderParamsSchema = z.object({
  id: z.string().min(1),
});

export type UpdateQuestionnaireBuilderParams = z.infer<
  typeof UpdateQuestionnaireBuilderParamsSchema
>;

/**
 * One section as the builder submits it — ported field for field from the legacy
 * `updateBuilder.body.properties.sections.items`.
 *
 * `id` is **required and client-generated**: the builder mints an id for a new
 * section and re-sends the stored id for an existing one, and the write is an
 * `ON CONFLICT (id) DO UPDATE` upsert keyed on it. A section id absent from the
 * payload is deleted, so the array is the complete desired state, not a patch.
 */
export const UpdateBuilderSectionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(1024),
  description: z.union([z.string().max(1024), z.null()]).optional(),
  sortOrder: z.number().min(1),
});

export type UpdateBuilderSection = z.infer<typeof UpdateBuilderSectionSchema>;

/**
 * One question as the builder submits it — ported from the legacy
 * `updateBuilder.body.properties.questions.items`.
 *
 * Three fields need care, because the legacy JSON Schema and what the frontends
 * actually send disagree, and Fastify's AJV `coerceTypes` papered over the gap:
 *
 * - **`retracedDatapointId` accepts `null`.** The legacy schema declared a bare
 *   `{ type: 'string' }`, but both frontends send `null` for a custom question
 *   (`retracedDatapointId: q.retracedDatapointId ?? null` in frontend-v2's
 *   `serializeQuestion`, `?? null` in the old app's
 *   `transformQuestionsToPayload`). AJV coerced that `null` to `''`, which the
 *   DB layer then turned back into `null`. Zod does not coerce, so declaring
 *   the union is what keeps every custom question savable.
 * - **`sectionId` is required but nullable.** The old app deliberately sends
 *   `sectionId: null` for every question when the questionnaire has only the
 *   default section (`transformToPayload`), so null is a real, load-bearing
 *   value rather than an oversight.
 * - **`id` is required**, where the legacy schema left it optional. It is the
 *   `ON CONFLICT` key of the upsert and the primary key of the row, so a payload
 *   omitting it could only ever fail on the NOT NULL constraint — a 500. Both
 *   frontends mint an id for every new question, so requiring it turns an
 *   unreachable crash into a 400.
 *
 * `meta` is a loose object: it carries the answer options, `isCompulsory`, and —
 * for an attachment question — the five `attachmentFile*` keys the service turns
 * into a `files` row.
 */
export const UpdateBuilderQuestionSchema = z.object({
  id: z.string().min(1),
  sectionId: z.union([z.string(), z.null()]),
  title: z.string().min(1).max(500),
  description: z.union([z.string().max(10000), z.null()]).optional(),
  questionTypeId: z.string(),
  retracedDatapointId: z.union([z.string(), z.null()]).optional(),
  isCustom: z.boolean(),
  meta: z.union([z.looseObject({}), z.null()]).optional(),
  sortOrder: z.number().min(1).optional(),
});

export type UpdateBuilderQuestion = z.infer<typeof UpdateBuilderQuestionSchema>;

/**
 * Body of `PUT /api/questionnaires/:id/builder` — the builder's save, for both a
 * questionnaire and a template.
 *
 * Unlike `PATCH /api/questionnaires/:id`, this is **not** a merge. `sections`
 * and `questions` describe the questionnaire's complete desired content: rows
 * present in the payload are upserted, rows absent from it are deleted (along
 * with their responses and comments). `name`, `description` and `dueDate` are
 * always written.
 *
 * Three deliberate differences from the legacy `updateBuilder.body`:
 *
 * - **`sections` and `questions` are required.** The legacy schema listed only
 *   `name` and `dueDate` as required, but the service dereferenced both arrays
 *   unconditionally (`sections.map(...)`), so omitting either answered 500.
 *   Requiring them turns that into a 400. Both frontends always send both.
 * - **`status` is not accepted.** The legacy schema took it and the write threw
 *   it away — `updateQuestionnaireBuilder` always persists
 *   `status: questionnaireExists.status`, which the legacy integration test
 *   pinned explicitly. Zod strips unknown keys rather than rejecting them, so a
 *   caller still sending `status` is unaffected and the stored status still
 *   wins.
 * - **`dueDate` is optional and nullable**, where legacy required an ISO string.
 *   Two callers need that. A **template** has no deadline of its own: the retired
 *   `PUT /v1/templates/:id` accepted no `dueDate` at all and its handler forced
 *   the column to `null`, which the service still does for a template row. And a
 *   **questionnaire whose due date was cleared** through
 *   `PATCH /api/questionnaires/:id` — which accepts an explicit `null` — sends
 *   `dueDate: null` on its next builder save; against `z.iso.datetime()` that was
 *   a 400 with no way back. An omitted date clears the column, as the legacy
 *   UPDATE did (it wrote whatever the payload carried, unconditionally).
 */
export const UpdateQuestionnaireBuilderBodySchema = z.object({
  name: z.string().min(1).max(255),
  description: z.union([z.string(), z.null()]).optional(),
  dueDate: z.union([z.iso.datetime(), z.null()]).optional(),
  sections: z.array(UpdateBuilderSectionSchema),
  questions: z.array(UpdateBuilderQuestionSchema),
});

export type UpdateQuestionnaireBuilderBody = z.infer<
  typeof UpdateQuestionnaireBuilderBodySchema
>;
